import { useQuery } from "@tanstack/react-query";
import { BarChart } from "../../components/charts";
import { DashFootnote, TrendPanel } from "../../components/dashboard";
import { DocTypeCards, DxCard, FollowUpSection, KpiTile, UnitRankCard, UnitTable } from "../../components/dx";
import { StatsMap } from "../../components/StatsMap";
import { ConsultationsStatsPanel, IncidentsSummaryPanel } from "../../components/servicePanels";
import { fetchHourly, placeKey } from "../../lib/dashboard-data";
import { APP_SHARE_NA, moneyParts } from "../../lib/dx";
import { num } from "../../lib/format";
import { shapeOf, useMapGeo, type MapMode } from "../../lib/map-geo";
import { canReviewConsultations } from "../../lib/services";
import { rangeKey } from "../../lib/stats-range";
import type { DashCtx } from "./ctx";

/**
 * ແຖບ ພາບລວມ (mockup `dx_overview.js`): KPI → ແຜນທີ່ → ຈັດອັນດັບ → ຕາຕະລາງ → ປະເພດເອກະສານ
 * → ແນວໂນ້ມ → ເລື່ອງທີ່ຕ້ອງຕິດຕາມ (ລຸ່ມສຸດ)
 */
export function OverviewTab({ ctx }: { ctx: DashCtx }) {
  const { range, place, here, data, breakdown, level, unit } = ctx;
  const s = data.summary.data;
  const today = range.kind === "today";
  const hourly = useQuery({
    enabled: today,
    queryKey: ["stats", "hourly", `${rangeKey(range)}|${placeKey(place)}`],
    queryFn: () => fetchHourly(range, place),
  });
  const rows = unit?.rows ?? [];
  const needsChanges = data.statuses.data
    ? (data.statuses.data.find((c) => c.status === "needs_changes")?.count ?? 0)
    : undefined;
  const money = s ? moneyParts(s.declared_fee_lak) : undefined;
  const anyError =
    data.summary.error ?? data.daily.error ?? data.statuses.error ?? data.docTypes.error ?? breakdown.error ?? hourly.error;
  const loaded = !breakdown.isPending;
  const sum = (f: (r: (typeof rows)[number]) => number) => rows.reduce((a, r) => a + f(r), 0);

  // การ์ดใบที่ 5 ตามระดับ (mockup): ตาแสงที่เปิดใช้ · จำนวนบ้าน · ค้างเกินกำหนด
  const fifth =
    level === "province" || level === "district" || level === "tasaeng" ? (
      <KpiTile
        label="ຕາແສງທີ່ເປີດໃຊ້ແລ້ວ"
        value={loaded ? `${String(sum((r) => r.tasaengs_staffed))}/${String(sum((r) => r.tasaengs))}` : undefined}
        unit="ຕາແສງ"
        tone="teal"
        hint={
          loaded
            ? sum((r) => r.tasaengs) === 0
              ? "ຂອບເຂດນີ້ຍັງບໍ່ມີຕາແສງໃນລະບົບ"
              : sum((r) => r.tasaengs) > sum((r) => r.tasaengs_staffed)
              ? `ຍັງບໍ່ເປີດ ${num(sum((r) => r.tasaengs) - sum((r) => r.tasaengs_staffed))} ຕາແສງ · ນັບຕາແສງທີ່ມີເຈົ້າໜ້າທີ່ປະຈຳ`
              : "ເປີດຄົບທຸກຕາແສງ"
            : undefined
        }
      />
    ) : level === "village" ? (
      <KpiTile label="ຈຳນວນບ້ານ" value={loaded ? num(sum((r) => r.villages)) : undefined} unit="ບ້ານ" tone="teal" hint="ໃນຕາແສງນີ້" />
    ) : (
      <KpiTile
        label="ຄ້າງເກີນກຳນົດ"
        value={s ? num(s.overdue_over_7d) : undefined}
        unit="ລາຍການ"
        tone="red"
        hint={s ? `ຈາກທີ່ກຳລັງດຳເນີນ ${num(s.pending)}` : undefined}
      />
    );

  // ແຜນທີ່: ລູກເປັນແຂວງ/ເມືອງ ທີ່ມີຮູບຮ່າງ (ທົ່ວປະເທດ) ຫຼື ລູກເປັນຕາແສງ (ມີຮູບໂດຍປະມານສະເພາະນະຄອນຫຼວງ)
  const geo = useMapGeo().data;
  const mapMode: MapMode | null = !geo
    ? null
    : level === "province"
      ? "country"
      : level === "district"
        ? "province"
        : level === "tasaeng"
          ? "district"
          : null;
  const hasShapes = Boolean(
    geo && mapMode && rows.some((r) => shapeOf(geo, mapMode, r.code, here.code)),
  );

  return (
    <>
      {anyError ? <div className="alert-error dx-gap-b">ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {anyError.message}</div> : null}

      <div className="dx-kpis dx-kpis--5">
        <KpiTile
          label={today ? "ຄຳຮ້ອງໃໝ່" : "ຈຳນວນຄຳຮ້ອງ"}
          value={s ? num(s.submitted) : undefined}
          unit="ຄຳຮ້ອງ"
          tone="navy"
          deltaPct={s?.submitted_delta_pct}
        />
        <KpiTile label="ເອກະສານທີ່ອອກແລ້ວ" value={s ? num(s.issued) : undefined} unit="ສະບັບ" tone="green" deltaPct={s?.issued_delta_pct} />
        {ctx.finance ? (
        <KpiTile
          label="ຄ່າທຳນຽມທີ່ຊຳລະແລ້ວ"
          value={money?.v}
          unit={money?.u}
          tone="gold"
          deltaPct={s?.fee_delta_pct}
          hint={s ? `${num(s.payments)} ລາຍການ · ນັບຕາມວັນທີ່ຈ່າຍ` : undefined}
        />
        ) : (
          // ຜູ້ກວດເອກະສານບໍ່ເບິ່ງການເງິນ (DSH-04) — ໃຊ້ບ່ອນນີ້ສະແດງວຽກທີ່ກຳລັງດຳເນີນແທນ
          <KpiTile
            label="ກຳລັງດຳເນີນການ"
            value={s ? num(s.pending) : undefined}
            unit="ລາຍການ"
            tone="gold"
            hint={
              s
                ? `ເກີນ 7 ມື້ ${num(s.overdue_over_7d)}${s.awaiting_village > 0 ? ` · ລໍນາຍບ້ານ ${num(s.awaiting_village)}` : ""} · ສະຖານະຕອນນີ້`
                : undefined
            }
            to="/requests"
          />
        )}
        <KpiTile label="ຍື່ນຜ່ານແອັບ" value={undefined} tone="ink" na={APP_SHARE_NA} />
        {fifth}
      </div>

      {geo && mapMode && hasShapes && unit ? (
        <div className="dx-gap">
          <StatsMap
            geo={geo}
            mode={mapMode}
            districtCode={mapMode === "district" ? here.code : undefined}
            rows={rows}
            childWord={unit.word}
            nameOf={unit.nameOf}
            today={today}
            finance={ctx.finance}
            onDrill={unit.onDrill}
          />
        </div>
      ) : null}

      {unit ? (
        <>
          <div className="dx-gap">
            <UnitRankCard u={unit} pending={breakdown.isPending} />
          </div>
          <div className="dx-gap">
            <UnitTable
              u={unit}
              here={here.name}
              level={level === "none" ? "village" : level}
              summary={s}
              pending={breakdown.isPending || data.summary.isPending}
            />
          </div>
        </>
      ) : null}

      <div className="dx-gap">
        <DocTypeCards items={data.docTypes.data ?? []} submitted={s?.submitted} pending={data.docTypes.isPending} />
      </div>

      <div className="dx-gap">
        {today ? (
          <DxCard title="ຄຳຮ້ອງຕາມຊົ່ວໂມງ (ມື້ນີ້)" sub="ເວລາລາວ · ນັບຮອດປັດຈຸບັນ">
            <BarChart
              series={[
                { label: "ຍື່ນຄຳຮ້ອງ", color: "var(--navy-600)", values: (hourly.data ?? []).map((h) => h.submitted) },
                { label: "ອອກເອກະສານ", color: "var(--success)", values: (hourly.data ?? []).map((h) => h.issued) },
              ]}
              labels={(hourly.data ?? []).map((h) => `${String(h.hour).padStart(2, "0")}:00`)}
              title="ກຣາຟແທ່ງຄຳຮ້ອງຕາມຊົ່ວໂມງ"
              emptyLabel={hourly.isPending ? "ກຳລັງໂຫຼດ…" : "ມື້ນີ້ຍັງບໍ່ມີຄຳຮ້ອງ"}
              format={(v) => `${num(v)} ໃບ`}
            />
          </DxCard>
        ) : (
          <TrendPanel rows={data.daily.data ?? []} weekly={data.weekly} pending={data.daily.isPending} fees={ctx.finance} />
        )}
      </div>

      <FollowUpSection summary={s} needsChanges={needsChanges} u={unit} />

      {/* ບໍລິການເສີມ (ADR-0027) — นับทั้งเขตของบัญชี ณ ตอนนี้ ไม่ขึ้นกับช่วงวันที่/การเจาะลง */}
      <div className="grid2 grid2--even dx-gap no-print">
        <IncidentsSummaryPanel />
        <ConsultationsStatsPanel showLink={canReviewConsultations()} />
      </div>

      <DashFootnote />
    </>
  );
}
